/**
 * Authentication Context
 * Manages authentication state and provides auth methods throughout the app
 * Implements Requirements 5.2, 5.3, 5.5
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabaseService } from '../services/supabaseClient';
import { linkAnalyticsToUser } from '../services/analyticsService';
import { UserProfile } from '../types';

interface AuthContextType {
  // Auth state
  user: User | null;
  profile: UserProfile | null;
  session: Session | null;
  loading: boolean;
  error: string | null;

  // Auth methods
  signInWithGoogle: () => Promise<void>;
  signInWithGitHub: () => Promise<void>;
  signInWithMicrosoft: () => Promise<void>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
  deleteAccount: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Initialize auth state on mount
   */
  useEffect(() => {
    // Get initial session
    const initializeAuth = async () => {
      try {
        const { session: currentSession, error: sessionError } =
          await supabaseService.getSession();

        if (sessionError) {
          console.error('Session error:', sessionError);
          setError(sessionError.message);
          setLoading(false);
          return;
        }

        if (currentSession?.user) {
          setSession(currentSession);
          setUser(currentSession.user);

          // Load user profile
          try {
            const userProfile = await supabaseService.getOrCreateUserProfile(
              currentSession.user
            );
            setProfile(userProfile);
          } catch (profileError) {
            console.error('Profile error:', profileError);
            setError(
              profileError instanceof Error
                ? profileError.message
                : 'Failed to load user profile'
            );
          }
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
        setError(err instanceof Error ? err.message : 'Authentication failed');
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth changes
    const { unsubscribe } = supabaseService.onAuthStateChange(
      async (event, newSession) => {
        console.log('Auth state changed:', event);

        setSession(newSession);
        setUser(newSession?.user || null);

        if (newSession?.user) {
          try {
            const userProfile = await supabaseService.getOrCreateUserProfile(
              newSession.user
            );
            setProfile(userProfile);

            // Fire-and-forget: link GA4 client ID + UTM params to user on sign-in
            if (event === 'SIGNED_IN') {
              linkAnalyticsToUser(
                newSession.user.id,
                supabaseService.getClient()
              );
            }
          } catch (profileError) {
            console.error('Profile error on auth change:', profileError);
            setError(
              profileError instanceof Error
                ? profileError.message
                : 'Failed to load user profile'
            );
          }
        } else {
          setProfile(null);
        }

        // Clear loading state after auth change
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, []);

  /**
   * Sign in with Google OAuth
   */
  const signInWithGoogle = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);

      const { error: signInError } = await supabaseService.signInWithOAuth('google');

      if (signInError) {
        setError(signInError.message);
        setLoading(false);
      }
      // Loading state will be cleared by onAuthStateChange
    } catch (err) {
      console.error('Google sign-in error:', err);
      setError(err instanceof Error ? err.message : 'Failed to sign in with Google');
      setLoading(false);
    }
  }, []);

  /**
   * Sign in with GitHub OAuth
   */
  const signInWithGitHub = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);

      const { error: signInError } = await supabaseService.signInWithOAuth('github');

      if (signInError) {
        setError(signInError.message);
        setLoading(false);
      }
      // Loading state will be cleared by onAuthStateChange
    } catch (err) {
      console.error('GitHub sign-in error:', err);
      setError(err instanceof Error ? err.message : 'Failed to sign in with GitHub');
      setLoading(false);
    }
  }, []);

  /**
   * Sign in with Microsoft OAuth
   */
  const signInWithMicrosoft = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);

      const { error: signInError } = await supabaseService.signInWithOAuth('azure');

      if (signInError) {
        setError(signInError.message);
        setLoading(false);
      }
      // Loading state will be cleared by onAuthStateChange
    } catch (err) {
      console.error('Microsoft sign-in error:', err);
      setError(err instanceof Error ? err.message : 'Failed to sign in with Microsoft');
      setLoading(false);
    }
  }, []);

  /**
   * Sign out the current user
   */
  const signOut = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);

      const { error: signOutError } = await supabaseService.signOut();

      if (signOutError) {
        setError(signOutError.message);
      }

      // Clear state
      setUser(null);
      setProfile(null);
      setSession(null);
    } catch (err) {
      console.error('Sign-out error:', err);
      setError(err instanceof Error ? err.message : 'Failed to sign out');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update user profile
   */
  const updateProfile = useCallback(
    async (updates: Partial<UserProfile>) => {
      if (!user) {
        setError('No user logged in');
        return;
      }

      try {
        setError(null);

        const updatedProfile = await supabaseService.updateUserProfile(user.id, {
          display_name: updates.displayName,
          avatar: updates.avatar,
          settings: updates.settings as unknown as Record<string, unknown>,
        });

        setProfile(updatedProfile);
      } catch (err) {
        console.error('Profile update error:', err);
        setError(err instanceof Error ? err.message : 'Failed to update profile');
        throw err;
      }
    },
    [user]
  );

  /**
   * Delete user account
   */
  const deleteAccount = useCallback(async () => {
    if (!user) {
      setError('No user logged in');
      return;
    }

    try {
      setError(null);
      setLoading(true);

      await supabaseService.deleteUserAccount(user.id);

      // Clear state
      setUser(null);
      setProfile(null);
      setSession(null);
    } catch (err) {
      console.error('Account deletion error:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete account');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: AuthContextType = {
    user,
    profile,
    session,
    loading,
    error,
    signInWithGoogle,
    signInWithGitHub,
    signInWithMicrosoft,
    signOut,
    updateProfile,
    deleteAccount,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to access auth context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
